import React, { useState, useEffect, useCallback } from "react";
import { observer } from "mobx-react";
import userDetail from "Stores/userDetail";
import makeApi, { URLS } from "Utils/apiURL";
import SearchInputByKeyword from "Components/common/SearchInputByKeyword";
import PaginationComponent from "Components/common/PaginationComponent";
import PageLimit from "Components/common/PageLimit";
import UsageAuditModal from "../../sequences/SequencePage/UsageAuditModal";
import ConnectedInboxSequenceCard from "./ConnectedInboxSequenceCard";

const SequenceCardSkeleton = () => (
  <div className="connectedInboxSequenceCard skeleton">
    <div className="cardMainContent">
      <div className="inboxInfo">
        <div className="skeleton-line skeleton-email"></div>
        <div className="skeleton-line skeleton-stats"></div>
      </div>
    </div>
    <div className="actionArea">
      <div className="skeleton-icon"></div>
    </div>
  </div>
);

const ConnectedInboxesSequences = observer((props = {}) => {
  const [inboxes, setInboxes] = useState([]);
  const [inboxUsage, setInboxUsage] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [pageNo, setPageNo] = useState(1);
  const [pageLimit, setPageLimit] = useState(10);
  const [selectedInboxForPopup, setSelectedInboxForPopup] = useState(null);

  const fetchInboxUsage = async () => {
    try {
      const response = await makeApi({
        url: URLS.getInboxUsage,
        method: "GET"
      });
      if (response?.data) {
        const usageMap = {};
        response.data.forEach(item => {
          usageMap[item.inboxId] = item;
        });
        setInboxUsage(usageMap);
      }
    } catch (error) {
      console.error("Error fetching inbox usage:", error);
    }
  };

  const loadData = async () => {
    setLoading(true);
    setInboxes(userDetail?.connectedInboxes || []);
    await fetchInboxUsage();
    setLoading(false);
  };

  useEffect(() => {
    loadData();
    return () => {
      setInboxes([]);
    };
  }, [userDetail?.connectedInboxes]);

  const handleSearch = (value) => {
    setSearchText(value);
    setPageNo(1);
  };

  const handleActionIconClick = useCallback((id, email) => {
    setSelectedInboxForPopup({ id, email });
  }, []);

  const closeInboxPopup = () => {
    setSelectedInboxForPopup(null);
  };

  const paginate = (pageNoObj = {}) => {
    setPageNo(pageNoObj?.selected + 1 || 1);
  };

  const changePageLimit = (limit) => {
    setPageLimit(limit);
    setPageNo(1);
  };

  const filteredInboxes = inboxes?.filter(
    item =>
      item?.status === "ACTIVE" &&
      item?.email.toLowerCase().includes(searchText.toLowerCase())
  ) || [];

  const totalPages = Math.ceil(filteredInboxes.length / pageLimit);
  const paginatedInboxes = filteredInboxes.slice((pageNo - 1) * pageLimit, pageNo * pageLimit);

  return (
    <div className="connectedInboxesSequencesTab">
      <div className="tabContentHeader">
        <div className="searchWrapper">
          <SearchInputByKeyword
            id="searchInbox"
            placeholder="Search by email"
            handleSearch={handleSearch}
          />
        </div>
      </div>

      <div className="sequencesListWrapper">
        {loading ? (
          <div className="sequencesCardContainer">
            {[1, 2, 3].map(i => <SequenceCardSkeleton key={i} />)}
          </div>
        ) : (
          <div className="sequencesCardContainer">
            {paginatedInboxes.length > 0 ? (
              paginatedInboxes.map(item => {
                const usage = inboxUsage[item.id] || {};
                return (
                  <ConnectedInboxSequenceCard
                    key={item.id}
                    id={item.id}
                    label={item.email}
                    totalSequences={usage.totalSequences || 0}
                    activeSequences={usage.activeSequences || 0}
                    onActionIconClick={handleActionIconClick}
                  />
                );
              })
            ) : (
              <div className="emptyState">No inboxes found</div>
            )}
          </div>
        )}
      </div>

      {(totalPages > 1 || filteredInboxes.length > 10) && (
        <div className="inboxPaginationAndLimit">
          <PaginationComponent
            callback={paginate}
            totalPages={totalPages || 1}
            currentPage={pageNo || 1}
            loading={loading}
          />
          <PageLimit
            limitPerPage={pageLimit}
            text="Showing"
            cbk={changePageLimit}
            totalCount={filteredInboxes.length}
          />
        </div>
      )}

      {selectedInboxForPopup && (
        <UsageAuditModal
          inboxId={selectedInboxForPopup.id}
          email={selectedInboxForPopup.email}
          onClose={closeInboxPopup}
        />
      )}
    </div>
  );
});

export default ConnectedInboxesSequences;
